/**
 * ══ LỊCH CHUYẾN — PHÉP TÍNH NGÀY THUẦN ═══════════════════════════════════════════════════════
 * Tách khỏi component để kiểm được mà không phải dựng React: mọi lỗi lịch đều là lỗi biên
 * (đầu tháng, cuối tháng, tuần vắt qua năm) và chúng chỉ lộ ra khi thử đúng những mốc ấy.
 *
 * ⚠ KHÔNG DÙNG `toISOString` — §3. Máy chủ ở UTC+7 nên mọi mốc trước 07:00 bị lùi một ngày, và
 *   một cái lịch lệch một ngày thì sai ở MỌI ô chứ không phải một ô.
 * ⚠ TUẦN BẮT ĐẦU THỨ HAI, không phải Chủ nhật. `Date.getDay()` trả 0 cho Chủ nhật — bảng của bản
 *   cũ xếp `T2 T3 T4 T5 T6 T7 CN`, nên phải đổi trục trước khi chia ô.
 */

export type Gran = 'day' | 'week' | 'month'

const p2 = (n: number): string => String(n).padStart(2, '0')

export const isoOf = (d: Date): string =>
  `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`

export const dateOf = (iso: string): Date => new Date(`${iso}T00:00:00`)

/** Thứ trong tuần theo trục THỨ HAI = 0 … CHỦ NHẬT = 6. */
export const mondayIndex = (d: Date): number => (d.getDay() + 6) % 7

export const startOfWeek = (iso: string): string => {
  const d = dateOf(iso)
  d.setDate(d.getDate() - mondayIndex(d))
  return isoOf(d)
}

export const addDays = (iso: string, n: number): string => {
  const d = dateOf(iso)
  d.setDate(d.getDate() + n)
  return isoOf(d)
}

export const addMonths = (iso: string, n: number): string => {
  const d = dateOf(iso)
  const day = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() + n)
  /* ⚠ KẸP NGÀY vào số ngày thật của tháng đích: 31/01 + 1 tháng mà không kẹp thì `Date` trôi
     sang 03/03. Một nút "tháng sau" nhảy hai tháng là lỗi không ai ngờ tới. */
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  d.setDate(Math.min(day, last))
  return isoOf(d)
}

/** Khoảng ngày mà một kỳ bao phủ — đúng thứ cửa `/az/legs/theo-ngay` cần. */
export const rangeOf = (anchor: string, gran: Gran): { from: string; to: string } => {
  if (gran === 'day') return { from: anchor, to: anchor }
  if (gran === 'week') {
    const from = startOfWeek(anchor)
    return { from, to: addDays(from, 6) }
  }
  const d = dateOf(anchor)
  const from = `${d.getFullYear()}-${p2(d.getMonth() + 1)}-01`
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  return { from, to: `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(last)}` }
}

/**
 * Lưới ô của một THÁNG — luôn bắt đầu ở thứ Hai và luôn đủ tuần trọn.
 * ⚠ Ô NGOÀI THÁNG VẪN CÓ MẶT (`inMonth: false`): bỏ chúng đi thì hàng đầu và hàng cuối thiếu ô và
 *   cả lưới lệch cột — ngày 15 rơi xuống dưới cột thứ Ba.
 */
export const monthGrid = (anchor: string): { iso: string; inMonth: boolean }[] => {
  const d = dateOf(anchor)
  const month = d.getMonth()
  const first = `${d.getFullYear()}-${p2(month + 1)}-01`
  const start = startOfWeek(first)
  const cells: { iso: string; inMonth: boolean }[] = []
  for (let i = 0; i < 42; i += 1) {
    const iso = addDays(start, i)
    cells.push({ iso, inMonth: dateOf(iso).getMonth() === month })
    /* Dừng sớm khi đã qua hết tháng và vừa khép một tuần — bản cũ không vẽ hàng trống thứ sáu. */
    if (i % 7 === 6 && dateOf(addDays(start, i)).getMonth() !== month && i >= 27) break
  }
  return cells
}
